import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { ModeToggle } from "@/components/site/ModeToggle";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { BrandLogo } from "@/components/site/BrandLogo";

export function Header() {
  const { t } = useLanguage();
  const { count, openDrawer } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";
  // On subpages, section anchors route back to the home page section.
  const base = onHome ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = [
    { key: "history", href: "#origins" },
    { key: "lineage", href: "#lineage" },
    { key: "houses", href: "#houses" },
    { key: "gallery", href: "#legacy" },
    { key: "tartan", href: "#tartan" },
    { key: "kindred", href: "#kindred" },
  ];

  const pages = [
    { to: "/notable", label: t.notable.nav, testid: "nav-notable" },
    { to: "/recipes", label: t.recipes?.nav || "Recipes", testid: "nav-recipes" },
    { to: "/events", label: "Events", testid: "nav-events" },
  ];

  const linkCls = (accent) =>
    `group relative whitespace-nowrap font-mono text-[10.5px] uppercase tracking-[0.06em] transition-colors duration-300 hover:text-clan-ink 2xl:text-[11px] 2xl:tracking-[0.14em] ${
      accent ? "text-clan-accent" : "text-clan-muted"
    }`;
  const underline = (
    <span className="absolute -bottom-1.5 left-1/2 h-px w-0 -translate-x-1/2 bg-clan-accent transition-all duration-300 group-hover:w-full" />
  );

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 z-50 w-full transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "border-b border-clan-ink/10 bg-clan-bg/70 backdrop-blur-xl py-3"
          : "border-b border-transparent py-5"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-2.5 sm:gap-4 sm:px-6 lg:px-10">
        <a href={onHome ? "#top" : "/"} data-testid="brand-logo" className="shrink-0">
          <BrandLogo size="header" />
        </a>

        <nav className="hidden min-w-0 flex-1 flex-col items-center justify-center gap-y-2.5 xl:flex">
          {/* Row 1 — heritage sections */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 2xl:gap-x-7">
            {nav.map((n) => (
              <a
                key={n.key}
                href={`${base}${n.href}`}
                data-testid={`nav-${n.key}`}
                className={linkCls(false)}
              >
                {t.nav[n.key]}
                {underline}
              </a>
            ))}
          </div>
          {/* Row 2 — directory & page links */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 2xl:gap-x-7">
            {pages.map((p) => (
              <Link key={p.to} to={p.to} data-testid={p.testid} className={linkCls(true)}>
                {p.label}
                {underline}
              </Link>
            ))}
          </div>
        </nav>

        <div className="flex shrink-0 items-center gap-1 sm:gap-3">
          <ModeToggle />
          <ThemeToggle />
          <LanguageSelector />

          {/* Store hidden for now — cart entry point disabled to keep the shop invisible.
              Restore this button (and the nav "Store" link + /shop mobile link) to re-enable. */}
          {false && (
            <button
              data-testid="header-cart-btn"
              onClick={openDrawer}
              aria-label="Open basket"
              className="relative grid h-8 w-8 place-items-center rounded-md border border-clan-ink/15 text-clan-ink transition-colors duration-300 hover:border-clan-accent/60 sm:h-9 sm:w-9"
            >
              <ShoppingBag className="h-4 w-4" />
              {count > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-md bg-clan-primary px-1 font-mono text-[9px] font-bold leading-none text-white"
                >
                  {count}
                </span>
              )}
            </button>
          )}

          <a
            href={`${base}#join`}
            data-testid="header-join-cta"
            className="hidden rounded-md bg-clan-primary px-5 py-2 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:bg-clan-primaryHover focus:outline-none focus:ring-2 focus:ring-clan-accent xl:inline-block"
          >
            <span className="pearl-btn">{t.nav.join}</span>
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="grid h-8 w-8 place-items-center rounded-md border border-clan-ink/15 text-clan-ink sm:h-9 sm:w-9 xl:hidden"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-clan-ink/10 bg-clan-bg/95 backdrop-blur-xl xl:hidden"
          >
            <div className="flex flex-col items-center gap-1 px-6 py-4 text-center">
              {[...nav, { key: "join", href: "#join" }].map((n) => (
                <a
                  key={n.key}
                  href={`${base}${n.href}`}
                  data-testid={`mobile-nav-${n.key}`}
                  onClick={() => setMobileOpen(false)}
                  className="w-full border-b border-clan-ink/5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-clan-muted transition-colors hover:text-clan-accent"
                >
                  {t.nav[n.key]}
                </a>
              ))}
              <Link
                to="/notable"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-notable"
                className="w-full py-3 font-mono text-xs uppercase tracking-[0.2em] text-clan-accent transition-colors hover:text-clan-ink"
              >
                {t.notable.link}
              </Link>
              <Link
                to="/recipes"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-recipes"
                className="w-full py-3 font-mono text-xs uppercase tracking-[0.2em] text-clan-accent transition-colors hover:text-clan-ink"
              >
                {t.recipes?.nav || "Recipes"}
              </Link>
              <Link
                to="/events"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-events"
                className="w-full py-3 font-mono text-xs uppercase tracking-[0.2em] text-clan-accent transition-colors hover:text-clan-ink"
              >
                Events
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
